// Authentication service for handling login and authentication state

const API_BASE_URL =
  process.env.REACT_APP_API_URL;

if (!process.env.REACT_APP_API_URL) {
  console.warn(
    "REACT_APP_API_URL environment variable is not set. API calls may fail."
  );
}

// Input sanitization helper
const sanitizeInput = (input) => {
  if (typeof input !== "string") return "";
  return input.trim().replace(/[<>]/g, "");
};

// JWT decode helper
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const fetchWithTimeout = async (url, options, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

// Secure token storage
const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem("accessToken", accessToken);
  if (refreshToken) {
    localStorage.setItem("refreshToken", refreshToken);
  } else {
    localStorage.removeItem("refreshToken");
  }
  // Clear any invalid flag on successful token set
  try {
    localStorage.removeItem("refreshTokenInvalid");
  } catch { }
};

const removeTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth:loggedOut"));
    }
  } catch { }
};

export const login = async (credentials) => {
  try {
    // Sanitize input credentials
    const sanitizedCredentials = {
      username: sanitizeInput(credentials.username),
      password: sanitizeInput(credentials.password),
    };

    // Validate sanitized input
    if (!sanitizedCredentials.username || !sanitizedCredentials.password) {
      throw new Error("Username and password are required");
    }

    const response = await fetch(`${API_BASE_URL}/auth/v2/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sanitizedCredentials),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData.message ||
        errorData.error ||
        (response.status === 401 || response.status === 400
          ? "Incorrect username or password."
          : `Login failed with status: ${response.status}`);
      throw new Error(message);
    }

    const data = await response.json();

    if (!data.sessionToken) {
      throw new Error("Invalid response from server - missing session token");
    }

    return data;
  } catch (error) {
    console.error("Login Step 1 error details:", error);

    // Provide more specific error messages
    if (error.name === "TypeError" && error.message.includes("fetch")) {
      throw new Error(
        "Network error: Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    throw error;
  }
};

export const verifyOtp = async ({ sessionToken, otpCode }) => {
  try {
    const sanitizedOtpCode = sanitizeInput(otpCode);

    if (!sessionToken) {
      throw new Error("Session token is missing or expired. Please log in again.");
    }
    if (!sanitizedOtpCode) {
      throw new Error("OTP code is required");
    }

    const response = await fetch(`${API_BASE_URL}/auth/v2/verify-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sessionToken,
        otpCode: sanitizedOtpCode,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let message = errorData.message || errorData.error;

      if (!message) {
        if (response.status === 400 || response.status === 401) {
          message = "Invalid, expired, or already used OTP code.";
        } else {
          message = `OTP verification failed with status: ${response.status}`;
        }
      }
      throw new Error(message);
    }

    const data = await response.json();

    // Validate response structure
    if (!data.accessToken) {
      throw new Error("Invalid response from server - missing access token");
    }

    // Store tokens securely
    setTokens(data.accessToken, data.refreshToken);
    try {
      localStorage.setItem("lastAccessTokenRefreshAt", String(Date.now()));
    } catch { }

    // Store user data if provided
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    return data;
  } catch (error) {
    console.error("OTP verification error details:", error);
    removeTokens();

    if (error.name === "TypeError" && error.message.includes("fetch")) {
      throw new Error(
        "Network error: Unable to connect to the server. Please check your internet connection and try again."
      );
    }

    throw error;
  }
};

export const logout = () => {
  removeTokens();
};

export const isAuthenticated = () => {
  const token = localStorage.getItem("accessToken");
  const hasAccessToken = !!token;
  const accessTokenExpired = hasAccessToken ? isTokenExpired() : true;
  const refreshTokenValid = isRefreshTokenValid();

  const isAuth = refreshTokenValid;

  console.log("Authentication check:", {
    hasAccessToken,
    accessTokenExpired,
    refreshTokenValid,
    isAuthenticated: isAuth,
  });

  return isAuth;
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem("user");
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

// Debug function to check JWT token contents
export const debugToken = () => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  try {
    const payload = parseJwt(token);
    return payload;
  } catch (error) {
    console.error("Error parsing JWT token:", error);
    return null;
  }
};

export const getAuthToken = () => {
  return localStorage.getItem("accessToken");
};

export const isTokenExpired = () => {
  const token = getAuthToken();
  if (!token) return true;

  const payload = parseJwt(token);
  // If token cannot be parsed as JWT, assume not expired (opaque token)
  if (!payload) return false;
  if (!payload.exp) return true;

  // Add 1 minute buffer before expiration (reduced from 2 minutes)
  return Date.now() >= payload.exp * 1000 - 1 * 60 * 1000;
};

export const isTokenExpiringSoon = () => {
  const token = getAuthToken();
  if (!token) return true;

  const payload = parseJwt(token);
  if (!payload) return false; // If not a JWT, assume not expiring soon
  if (!payload.exp) return true;

  // Check if token will expire in the next 3 minutes (reduced from 5 minutes)
  return Date.now() >= payload.exp * 1000 - 3 * 60 * 1000;
};

// Check if refresh token is valid and not expired
export const isRefreshTokenValid = () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      console.log(
        "No refresh token found in localStorage; assuming cookie-based refresh may be available"
      );
      return true;
    }
    console.log("No refresh token found in localStorage");
    return false;
  }

  // If previously marked invalid due to server rejection, treat as invalid
  try {
    if (localStorage.getItem("refreshTokenInvalid") === "1") {
      console.log(
        "Refresh token marked as invalid from previous server rejection"
      );
      return false;
    }
  } catch { }

  // If the refresh token looks like a JWT, validate expiration; otherwise assume opaque and valid
  const looksLikeJwt = refreshToken.split(".").length === 3;
  if (!looksLikeJwt) {
    console.log("Refresh token appears to be opaque token - assuming valid");
    return true;
  }

  const payload = parseJwt(refreshToken);
  if (!payload || !payload.exp) {
    console.log(
      "Refresh token cannot be parsed or has no expiration - assuming valid"
    );
    return true; // if cannot parse, be lenient
  }

  // Check if refresh token is expired (with 1 minute buffer)
  const isExpired = Date.now() >= payload.exp * 1000 - 1 * 60 * 1000;
  if (isExpired) {
    console.log("Refresh token is expired:", new Date(payload.exp * 1000));
  } else {
    console.log(
      "Refresh token is valid, expires at:",
      new Date(payload.exp * 1000)
    );
  }
  return !isExpired;
};

export const getRefreshTokenExpiryTime = () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    if (localStorage.getItem("refreshTokenInvalid") === "1") {
      return Date.now();
    }
  } catch { }

  const looksLikeJwt = refreshToken.split(".").length === 3;
  if (!looksLikeJwt) {
    return null;
  }

  const payload = parseJwt(refreshToken);
  if (!payload || !payload.exp) {
    return null;
  }

  return payload.exp * 1000 - 1 * 60 * 1000;
};

// Single-flight refresh state within auth module to avoid duplicate refresh calls across consumers
let authModuleIsRefreshing = false;
let authModuleRefreshPromise = null;

export const resetRefreshState = () => {
  authModuleIsRefreshing = false;
  authModuleRefreshPromise = null;
};

// Function to validate current token state and return detailed status
export const validateTokenState = () => {
  const accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  const accessTokenValid = accessToken && !isTokenExpired();
  const refreshTokenValid = isRefreshTokenValid();

  return {
    hasAccessToken: !!accessToken,
    hasRefreshToken: !!refreshToken,
    accessTokenValid,
    refreshTokenValid,
    canRefresh: refreshTokenValid,
    needsLogin: !refreshTokenValid,
  };
};

let ensureValidAccessTokenPromise = null;

export const ensureValidAccessToken = async (context = "") => {
  if (ensureValidAccessTokenPromise) return ensureValidAccessTokenPromise;
  ensureValidAccessTokenPromise = (async () => {
    const tokenState = validateTokenState();
    const accessTokenExpired = isTokenExpired();
    const accessTokenExpiringSoon = isTokenExpiringSoon();
    const hasAccessToken = tokenState.hasAccessToken;
    const canRefresh = tokenState.canRefresh;
    console.info("Auth preflight", {
      context,
      hasAccessToken,
      accessTokenExpired,
      accessTokenExpiringSoon,
      canRefresh,
    });

    if (hasAccessToken && !accessTokenExpired && !accessTokenExpiringSoon) {
      return { ok: true, reason: "access_valid" };
    }

    if (!hasAccessToken && !canRefresh) {
      return { ok: false, reason: "no_tokens" };
    }

    try {
      const token = await refreshAccessToken();
      return { ok: true, reason: "refreshed", token };
    } catch (error) {
      const message = error?.message || "";
      const isAuthError =
        message.includes("Session expired") ||
        message.includes("Refresh token is invalid") ||
        message.includes("Invalid refresh response") ||
        message.includes("invalid or expired");
      const isNetworkError =
        message.includes("Network error") ||
        message.includes("Failed to fetch") ||
        message.includes("timeout") ||
        message.includes("aborted");
      console.error("Auth preflight refresh failed", { context, message });
      return {
        ok: false,
        reason: isAuthError
          ? "refresh_failed_invalid"
          : isNetworkError
            ? "refresh_failed_network"
            : "refresh_failed",
        error,
      };
    }
  })();

  try {
    return await ensureValidAccessTokenPromise;
  } finally {
    ensureValidAccessTokenPromise = null;
  }
};

// Function to handle initial app load token validation
export const initializeTokenValidation = async () => {
  console.log("Starting initial token validation...");

  const tokenState = validateTokenState();
  console.log("Current token state:", tokenState);

  // If no refresh token and no access token, user needs to login
  if (!tokenState.hasRefreshToken && !tokenState.hasAccessToken) {
    console.log("No refresh token or access token found - user needs to login");
    removeTokens();
    console.log("Tokens cleared from localStorage");
    return { success: false, reason: "no_refresh_token" };
  }

  // If refresh token is invalid/expired (regardless of access token), user needs to login
  if (!tokenState.refreshTokenValid) {
    console.log("Refresh token is invalid/expired - user needs to login");
    removeTokens();
    console.log(
      "Tokens cleared from localStorage due to expired refresh token"
    );
    return { success: false, reason: "refresh_token_expired" };
  }

  // If we have a valid refresh token but no valid access token, try to refresh
  if (!tokenState.accessTokenValid && tokenState.canRefresh) {
    try {
      console.log(
        "Access token invalid but refresh token valid - attempting refresh"
      );
      await refreshAccessToken();
      console.log("Token refresh successful during initialization");
      return { success: true, reason: "refreshed" };
    } catch (error) {
      console.error("Failed to refresh token during initialization:", error);

      // If refresh fails due to invalid refresh token, logout
      if (
        error.message.includes("No refresh token") ||
        error.message.includes("Refresh token is invalid") ||
        error.message.includes("invalid or expired") ||
        error.message.includes("Session expired")
      ) {
        console.log(
          "Refresh token invalid during refresh attempt - user needs to login"
        );
        removeTokens();
        return { success: false, reason: "refresh_failed_invalid_token" };
      }

      // For network errors, we'll keep the user logged in but mark as not authenticated
      console.log(
        "Network error during refresh - keeping user logged in but not authenticated"
      );
      return { success: false, reason: "network_error" };
    }
  }

  // If we have both valid tokens, we're good
  if (tokenState.accessTokenValid && tokenState.refreshTokenValid) {
    console.log("Both tokens are valid - authentication successful");
    return { success: true, reason: "valid_tokens" };
  }

  // Fallback case
  console.log("Unexpected token state - user needs to login");
  removeTokens();
  return { success: false, reason: "unexpected_state" };
};

export const refreshAccessToken = async () => {
  if (authModuleIsRefreshing && authModuleRefreshPromise) {
    return authModuleRefreshPromise;
  }
  const refreshToken = localStorage.getItem("refreshToken");
  const hasRefreshToken = !!refreshToken;

  // Validate refresh token format and expiration
  if (hasRefreshToken && !isRefreshTokenValid()) {
    console.error("Refresh token is invalid or expired");
    // Only clear tokens if refresh token is actually invalid
    try {
      localStorage.setItem("refreshTokenInvalid", "1");
    } catch { }
    removeTokens();
    throw new Error("Refresh token is invalid or expired");
  }

  try {
    authModuleIsRefreshing = true;
    authModuleRefreshPromise = (async () => {
      // Use POST with JSON body; many servers reject GET bodies
      const response = await fetchWithTimeout(`${API_BASE_URL}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain, */*",
        },
        body: JSON.stringify(hasRefreshToken ? { token: refreshToken } : {}),
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Token refresh failed:", response.status, errorData);

        const serverMessage =
          (errorData && (errorData.message || errorData.error)) || "";
        const isInvalidRefreshFromServer =
          response.status === 400 &&
          typeof serverMessage === "string" &&
          serverMessage.toLowerCase().includes("invalid or expired");

        // Clear tokens on specific authentication errors and mark refresh as invalid
        if (response.status === 401 || response.status === 403 || isInvalidRefreshFromServer) {
          try {
            localStorage.setItem("refreshTokenInvalid", "1");
          } catch { }
          removeTokens();
          throw new Error("Refresh token is invalid or expired");
        } else {
          // For other errors (like network issues), don't clear tokens immediately
          throw new Error(`Token refresh failed: ${response.status}`);
        }
      }

      const data = await response.json();

      if (!data.accessToken) {
        console.error("Invalid refresh response - no access token");
        // Do not clear refresh token; only clear access token
        localStorage.removeItem("accessToken");
        throw new Error("Invalid refresh response");
      }

      // Update tokens
      localStorage.setItem("accessToken", data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      try {
        localStorage.removeItem("refreshTokenInvalid");
      } catch { }
      try {
        localStorage.setItem("lastAccessTokenRefreshAt", String(Date.now()));
      } catch { }
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("auth:tokenRefreshed"));
        }
      } catch { }

      return data.accessToken;
    })();

    const result = await authModuleRefreshPromise;
    return result;
  } catch (error) {
    console.error("Token refresh error:", error);
    // Only remove tokens if it's a clear authentication error
    if (
      error.message.includes("Session expired") ||
      error.message.includes("Refresh token is invalid") ||
      error.message.includes("Invalid refresh response") ||
      error.message.includes("invalid or expired")
    ) {
      try {
        localStorage.setItem("refreshTokenInvalid", "1");
      } catch { }
      removeTokens();
    }
    throw error;
  } finally {
    authModuleIsRefreshing = false;
    authModuleRefreshPromise = null;
  }
};
